import { z } from "zod";

import {
  analysisCategorySchema,
  analysisConfidenceSchema,
  analysisDiffCategorySchema,
  analysisDiffFindingIdSchema,
  analysisDiffKindSchema,
  analysisFindingIdSchema,
  analysisGradeSchema,
  analysisRiskFactorIdSchema,
  analysisRiskLevelSchema,
  analysisSeveritySchema,
} from "../analysis-findings.js";
import { qlikEvaluationSchema, qlikEvaluationTimeoutSecSchema } from "./qlik-evaluation.js";

// --- Usage reference (where an item is used) ---

export const analysisUsageRefSchema = z.object({
  objectId: z.string(),
  objectType: z.string(),
  objectTitle: z.string(),
  context: z.string().optional(), // e.g. "dimension", "measure expression", "calc condition"
});

export type AnalysisUsageRef = z.infer<typeof analysisUsageRefSchema>;

// --- Expression issue (result of QIX Engine CheckExpression / CheckNumberOrExpression) ---

export const expressionIssueSchema = z.object({
  errorMsg: z.string().default(""),
  badFieldNames: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  dangerousFieldNames: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
});

export type ExpressionIssue = z.infer<typeof expressionIssueSchema>;

// --- Field analysis ---

export const analysisFieldSchema = z.object({
  name: z.string(),
  tableName: z.string(),
  tableNames: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  cardinality: z.number(),
  byteSize: z.number(),
  indexByteSize: z.number().default(0),
  totalByteSize: z.number().default(0),
  tags: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  isKey: z.boolean().default(false),
  isKeyText: z.boolean().default(false),
  isHidden: z.boolean().default(false),
  usageCount: z.number(),
  usedIn: z
    .array(analysisUsageRefSchema)
    .nullish()
    .transform((v) => v ?? []),
  isIndirectUsage: z.boolean().default(false),
  isOrphanUsageOnly: z.boolean().default(false),
});

export type AnalysisField = z.infer<typeof analysisFieldSchema>;

// --- Variable analysis ---

export const analysisVariableSchema = z.object({
  id: z.string(),
  name: z.string(),
  definition: z.string().optional(),
  isUsed: z.boolean(),
  usageCount: z.number(),
  usedIn: z
    .array(analysisUsageRefSchema)
    .nullish()
    .transform((v) => v ?? []),
  isIndirectUsage: z.boolean().default(false),
  isOrphanUsageOnly: z.boolean().default(false),
  expressionIssues: expressionIssueSchema.optional(),
});

export type AnalysisVariable = z.infer<typeof analysisVariableSchema>;

// --- Dimension analysis ---

export const analysisDimensionSchema = z.object({
  id: z.string(),
  title: z.string(),
  fieldDefs: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  usedInCount: z.number(),
  usedIn: z
    .array(analysisUsageRefSchema)
    .nullish()
    .transform((v) => v ?? []),
  isIndirectUsage: z.boolean().default(false),
  isOrphanUsageOnly: z.boolean().default(false),
  isBroken: z.boolean().default(false),
  brokenRefs: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  expressionIssues: expressionIssueSchema.optional(),
});

export type AnalysisDimension = z.infer<typeof analysisDimensionSchema>;

// --- Expression risk (object complexity) ---

export const analysisRiskFactorSchema = z.object({
  id: analysisRiskFactorIdSchema,
  // Same value shape as a finding's metrics; absent on the Go side when empty.
  metrics: z.record(z.string(), z.union([z.number(), z.string()])).optional(),
});

export type AnalysisRiskFactor = z.infer<typeof analysisRiskFactorSchema>;

// Risk roll-up carried by measures, objects and object expressions alike.
// Defaults keep analyses stored before expression risk existed parsing.
const riskFields = {
  riskScore: z.number().default(0),
  riskLevel: analysisRiskLevelSchema.default("none"),
  riskFactors: z
    .array(analysisRiskFactorSchema)
    .nullish()
    .transform((v) => v ?? []),
};

export const analysisObjectExpressionSchema = z.object({
  expression: z.string(),
  ...riskFields,
});

export type AnalysisObjectExpression = z.infer<typeof analysisObjectExpressionSchema>;

// --- Measure analysis ---

export const analysisMeasureSchema = z.object({
  id: z.string(),
  title: z.string(),
  expression: z.string().optional(),
  usedInCount: z.number(),
  usedIn: z
    .array(analysisUsageRefSchema)
    .nullish()
    .transform((v) => v ?? []),
  isIndirectUsage: z.boolean().default(false),
  isOrphanUsageOnly: z.boolean().default(false),
  isBroken: z.boolean().default(false),
  brokenRefs: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
  expressionIssues: expressionIssueSchema.optional(),
  ...riskFields,
});

export type AnalysisMeasure = z.infer<typeof analysisMeasureSchema>;

// --- Object inventory ---

/**
 * Where an object's title came from. Most Qlik types (KPI, filterpane,
 * text-image, container, extension) carry no author-typed title, so the worker
 * walks a ladder down to the single master item or field the object references.
 */
export const analysisTitleSourceSchema = z.enum([
  "own",
  "dynamic",
  "master",
  "measure",
  "dimension",
  "field",
  "none",
]);

export type AnalysisTitleSource = z.infer<typeof analysisTitleSourceSchema>;

export const analysisObjectSchema = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string(),
  // Analyses stored before the ladder existed have no key, and every title they
  // hold is an authored one — so "own" is the correct reading of their absence.
  titleSource: analysisTitleSourceSchema.default("own"),
  sheetId: z.string().optional(),
  // Object risk is the roll-up of its expressions: score is the maximum,
  // riskFactors the deduplicated union.
  ...riskFields,
  expressions: z
    .array(analysisObjectExpressionSchema)
    .nullish()
    .transform((v) => v ?? []),
});

export type AnalysisObject = z.infer<typeof analysisObjectSchema>;

// --- Summary ---

export const analysisSummarySchema = z.object({
  totalFields: z.number(),
  usedFields: z.number(),
  unusedFields: z.number(),
  totalBytes: z.number(),
  indexBytes: z.number().default(0),
  dataBytes: z.number().default(0),
  wastedBytes: z.number(),
  wastedIndexBytes: z.number().default(0),
  indirectWastedBytes: z.number().default(0),
  indirectWastedIndexBytes: z.number().default(0),
  orphanWastedBytes: z.number().default(0),
  orphanWastedIndexBytes: z.number().default(0),
  staticByteSize: z.number().default(0),
  appFileSize: z.number().default(0),
  totalVariables: z.number(),
  usedVariables: z.number(),
  unusedVariables: z.number(),
  totalDimensions: z.number(),
  usedDimensions: z.number(),
  totalMeasures: z.number(),
  usedMeasures: z.number(),
  brokenDimensions: z.number().default(0),
  brokenMeasures: z.number().default(0),
  totalObjects: z.number(),
});

export type AnalysisSummary = z.infer<typeof analysisSummarySchema>;

// --- Health (findings + scoring) ---

export const analysisRefSchema = z.object({
  type: z.string(),
  id: z.string(),
  label: z.string(),
});

export type AppAnalysisRef = z.infer<typeof analysisRefSchema>;

export const analysisFindingSchema = z.object({
  id: analysisFindingIdSchema,
  severity: analysisSeveritySchema,
  confidence: analysisConfidenceSchema,
  category: analysisCategorySchema,
  metrics: z.record(z.string(), z.union([z.number(), z.string()])),
  refs: z.array(analysisRefSchema),
});

export type AppAnalysisFinding = z.infer<typeof analysisFindingSchema>;

export const analysisHealthCategorySchema = z.object({
  id: analysisCategorySchema,
  score: z.number(),
  findingIds: z.array(analysisFindingIdSchema),
});

export type AppAnalysisHealthCategory = z.infer<typeof analysisHealthCategorySchema>;

export const analysisHealthSchema = z.object({
  // Payload generation (Go: HealthPayloadVersion). Absent/1 = phase-1 payload,
  // where object-complexity was not scored; 2 = every category is scored.
  version: z.number().int().default(1),
  score: z.number(),
  grade: analysisGradeSchema,
  topFindings: z.array(analysisFindingIdSchema),
  categories: z.array(analysisHealthCategorySchema),
  findings: z.array(analysisFindingSchema),
});

export type AppAnalysisHealth = z.infer<typeof analysisHealthSchema>;

export const analysisHealthSummarySchema = z.object({
  score: z.number(),
  grade: analysisGradeSchema,
});

export type AppAnalysisHealthSummary = z.infer<typeof analysisHealthSummarySchema>;

// --- Our own object measurement (frente 3, session-cube protocol) ---
//
// These mirror the Go structs in apps/worker/internal/qlik/measurement_run.go
// (MeasurementResult / ObjectTiming / MeasureTiming) and measurement.go
// (MeasurementContext) field for field: what lands in the `object_timings`
// jsonb is exactly what those structs marshal, so a field that is `omitempty`
// there is `.optional()` here.

// How an object was measured: rebuilt as a session cube, timed through the real
// object's layout (list objects), or neither (text, buttons, containers).
//
// Order is alphabetical and MUST match the Go constants
// (apps/worker/internal/qlik/measurement.go). A parity test on the Go side
// asserts the two lists are identical.
export const ANALYSIS_OBJECT_DEF_KINDS = ["cube", "listObject", "noData"] as const;
export const analysisObjectDefKindSchema = z.enum(ANALYSIS_OBJECT_DEF_KINDS);
export type AnalysisObjectDefKind = z.infer<typeof analysisObjectDefKindSchema>;

// Completed readings, missing readings, and timeouts (a lower bound only).
// Same ordering rule and parity test as above.
export const ANALYSIS_OBJECT_TIMING_STATUSES = [
  "cheap",
  "notMeasured",
  "signal",
  "timeout",
] as const;
export const analysisObjectTimingStatusSchema = z.enum(ANALYSIS_OBJECT_TIMING_STATUSES);
export type AnalysisObjectTimingStatus = z.infer<typeof analysisObjectTimingStatusSchema>;

// Why something carries no reading. Same ordering rule and parity test.
// `calcCondition` (a condition not met in the context) and `notCalculable` (the
// engine rejects the definition in this app) are the app's own answers, not
// gaps: they never make a run, phase or context partial.
export const ANALYSIS_MEASUREMENT_SKIP_REASONS = [
  "budget",
  "calcCondition",
  "cancelled",
  "error",
  "noDefinition",
  "noMeasures",
  "notCalculable",
  "singleMeasure",
  "timeout",
] as const;
export const analysisMeasurementSkipReasonSchema = z.enum(ANALYSIS_MEASUREMENT_SKIP_REASONS);
export type AnalysisMeasurementSkipReason = z.infer<typeof analysisMeasurementSkipReasonSchema>;

export const ANALYSIS_MEASUREMENT_PHASES = [
  "objects",
  "cache",
  "deconstruction",
  "conditions",
] as const;
export const analysisMeasurementPhaseSchema = z.enum(ANALYSIS_MEASUREMENT_PHASES);
export type AnalysisMeasurementPhase = z.infer<typeof analysisMeasurementPhaseSchema>;
export const analysisMeasurementPhasesSchema = z
  .array(analysisMeasurementPhaseSchema)
  .min(1)
  .max(ANALYSIS_MEASUREMENT_PHASES.length)
  .refine((phases) => new Set(phases).size === phases.length, "As fases não podem se repetir.")
  .refine((phases) => phases.includes("objects"), "A medição de objetos é obrigatória.");

// Which precedence branch of the measurement context actually applied. Same
// ordering rule and parity test.
export const ANALYSIS_MEASUREMENT_CONTEXT_KINDS = ["bookmark", "clean", "selections"] as const;
export const analysisMeasurementContextKindSchema = z.enum(ANALYSIS_MEASUREMENT_CONTEXT_KINDS);
export type AnalysisMeasurementContextKind = z.infer<typeof analysisMeasurementContextKindSchema>;

export const analysisMeasurementSelectionSchema = z.object({
  field: z.string(),
  values: z
    .array(z.string())
    .nullish()
    .transform((v) => v ?? []),
});

export type AnalysisMeasurementSelection = z.infer<typeof analysisMeasurementSelectionSchema>;

// The selection state every timing in a run was taken under. A timing without
// it compares to nothing — the spike's three runs differ materially by state.
export const analysisMeasurementContextSchema = z.object({
  kind: analysisMeasurementContextKindSchema,
  selections: z
    .array(analysisMeasurementSelectionSchema)
    .nullish()
    .transform((v) => v ?? []),
  bookmarkId: z.string().optional(),
  bookmarkName: z.string().optional(),
  isDefaultBookmark: z.boolean().optional(),
});

export type AnalysisMeasurementContext = z.infer<typeof analysisMeasurementContextSchema>;

// A timing that was never taken carries NO number. Accepts the key being absent
// (Go's `omitempty` on a nil *int64) or an explicit null from a jsonb round
// trip, and REJECTS a number — so "not measured" is unrepresentable as 0 ms in
// the schema itself, not by convention.
const absentTiming = z.null().optional();
const timingLowerBoundSchema = z.number().int().nonnegative();

// Statuses that come WITH a reading. `cheap` is a real measurement under the
// floor, so it carries a number like `signal` does.
const measuredStatusSchema = z.enum(["cheap", "signal"]);

const measureTimingFields = {
  index: z.number().int(),
  label: z.string().optional(),
  // What was actually measured: the measure's expression after literal
  // $(variable) expansion.
  expression: z.string().optional(),
  reason: analysisMeasurementSkipReasonSchema.optional(),
  error: z.string().optional(),
};

// One measure of an object, measured in a cube of its own with the object's
// dimensions (the deconstruction).
const measureTimingSchema = z.discriminatedUnion("status", [
  z.object({
    ...measureTimingFields,
    status: measuredStatusSchema,
    coldMs: z.number().int(),
    lowerBoundMs: absentTiming,
  }),
  z.object({
    ...measureTimingFields,
    status: z.literal("notMeasured"),
    coldMs: absentTiming,
    lowerBoundMs: absentTiming,
  }),
  z.object({
    ...measureTimingFields,
    status: z.literal("timeout"),
    coldMs: absentTiming,
    lowerBoundMs: timingLowerBoundSchema,
  }),
]);

export const analysisMeasureTimingSchema = measureTimingSchema.transform((timing) => {
  // Older workers stored deadline failures as completed readings at the cap.
  if (
    (timing.status === "signal" || timing.status === "cheap") &&
    timing.error?.includes("context deadline exceeded")
  ) {
    const { coldMs, ...rest } = timing;
    return {
      ...rest,
      status: "timeout" as const,
      reason: "timeout" as const,
      lowerBoundMs: Math.max(0, coldMs),
    };
  }
  return timing;
});

export type AnalysisMeasureTiming = z.infer<typeof analysisMeasureTimingSchema>;

const objectTimingFields = {
  objectId: z.string(),
  type: z.string().optional(),
  title: z.string().optional(),
  sheetId: z.string().optional(),
  kind: analysisObjectDefKindSchema,
  // Position in the single measurement sequence. The first object of a sheet
  // pays the cold cost of expressions its neighbours share, so a timing is only
  // readable next to its order.
  order: z.number().int(),
  // An equivalent listbox supplied this result; it was not timed independently.
  reusedFromObjectId: z.string().optional(),
  hadCalcCondition: z.boolean().optional(),
  // A calculation condition is not met in this context: the object does not
  // calculate in the app, so it carries no reading (reason `calcCondition`).
  // Runs stored before this rule measured it without the condition instead.
  blockedByCondition: z.boolean().optional(),
  // Whether THIS object's calculation-condition verdict was obtained. The
  // budget can end before the object and a timeout or error is no verdict:
  // while this is false, `blockedByCondition: false` means "never checked",
  // not "not blocked". Absent — never false — on a run stored before the flag
  // existed, and those did probe, so only an explicit false means unchecked.
  conditionProbed: z.boolean().optional(),
  // The conditions phase: a cube blocked by its own condition, timed cold once
  // more without it — what the condition spares the app in this context. Kept
  // apart from the object's own reading, so no total or overrun counts it.
  unconditionedMs: z.number().int().optional(),
  unconditionedLowerBoundMs: timingLowerBoundSchema.optional(),
  measures: z.array(analysisMeasureTimingSchema).optional(),
  deconstructionSkipped: analysisMeasurementSkipReasonSchema.optional(),
  reason: analysisMeasurementSkipReasonSchema.optional(),
  error: z.string().optional(),
};

const objectTimingSchema = z.discriminatedUnion("status", [
  z
    .object({
      ...objectTimingFields,
      status: measuredStatusSchema,
      // Iteration 1 minus the session's median RTT — the reported cost.
      coldMs: z.number().int(),
      lowerBoundMs: absentTiming,
      // Iteration 2, same cube, no salt: confirms the engine caches it. Never
      // averaged with coldMs, and absent when the budget ended after pass 1.
      cachedMs: z.number().int().optional(),
      cachedLowerBoundMs: timingLowerBoundSchema.optional(),
    })
    .refine((timing) => timing.cachedMs === undefined || timing.cachedLowerBoundMs === undefined, {
      message: "A cached pass cannot be both completed and timed out",
      path: ["cachedLowerBoundMs"],
    }),
  z.object({
    ...objectTimingFields,
    status: z.literal("notMeasured"),
    coldMs: absentTiming,
    // No cold reading means the second pass never ran for this object either.
    cachedMs: absentTiming,
    lowerBoundMs: absentTiming,
    cachedLowerBoundMs: absentTiming,
  }),
  z.object({
    ...objectTimingFields,
    status: z.literal("timeout"),
    coldMs: absentTiming,
    cachedMs: absentTiming,
    lowerBoundMs: timingLowerBoundSchema,
    cachedLowerBoundMs: absentTiming,
  }),
]);

export const analysisObjectTimingSchema = objectTimingSchema.transform((timing) => {
  if (
    (timing.status === "signal" || timing.status === "cheap") &&
    timing.cachedLowerBoundMs === undefined &&
    timing.reason === undefined &&
    timing.error?.includes("context deadline exceeded")
  ) {
    if (timing.cachedMs !== undefined) {
      const { cachedMs, ...rest } = timing;
      return { ...rest, cachedLowerBoundMs: Math.max(0, cachedMs) };
    }
    const { coldMs, ...rest } = timing;
    return {
      ...rest,
      status: "timeout" as const,
      reason: "timeout" as const,
      lowerBoundMs: Math.max(0, coldMs),
      deconstructionSkipped: "timeout" as const,
    };
  }
  return timing;
});

export type AnalysisObjectTiming = z.infer<typeof analysisObjectTimingSchema>;

// The whole run, exactly as it is stored in the `object_timings` jsonb column.
export const analysisObjectTimingsSchema = z.object({
  context: analysisMeasurementContextSchema,
  // Absent on legacy results, which ran all phases.
  measurementPhases: analysisMeasurementPhasesSchema.optional(),
  // The floor subtracted from every reading, and what separates cheap from
  // signal (30 ms — the resolution the spike measured).
  medianRttMs: z.number(),
  thresholdMs: z.number(),
  countsByKind: z.partialRecord(analysisObjectDefKindSchema, z.number()),
  // True when at least one planned measurement did not happen.
  partial: z.boolean(),
  partialReason: analysisMeasurementSkipReasonSchema.optional(),
  // Only incomplete stages; absent on runs recorded before phase coverage.
  partialByPhase: z
    .partialRecord(analysisMeasurementPhaseSchema, analysisMeasurementSkipReasonSchema)
    .optional(),
  durationMs: z.number(),
  objects: z
    .array(analysisObjectTimingSchema)
    .nullish()
    .transform((v) => v ?? []),
});

export type AnalysisObjectTimings = z.infer<typeof analysisObjectTimingsSchema>;

const measurementDurationSecSchema = z.number().int().min(1).max(3600);

const measurementBookmarkIdSchema = z
  .string()
  .min(1)
  .max(256)
  .regex(/\S/)
  .describe(
    "ID do bookmark visível à identidade da conexão (público/publicado). Tem prioridade sobre o nome; não combine com seleções.",
  );
const measurementBookmarkNameSchema = z
  .string()
  .min(1)
  .max(512)
  .regex(/\S/)
  .describe(
    "Nome do bookmark visível à identidade da conexão (público/publicado). Não combine com seleções.",
  );
const measurementSelectionsSchema = z
  .array(
    analysisMeasurementSelectionSchema.extend({
      field: z.string().min(1).max(256).regex(/\S/),
      values: z
        .array(z.string().min(1).max(1024))
        .min(1)
        .max(100)
        .refine(
          (values) => new Set(values.map((value) => value.toLowerCase())).size === values.length,
          "Valores de seleção não podem se repetir, inclusive com diferenças de maiúsculas/minúsculas.",
        ),
    }),
  )
  .min(1)
  .max(20)
  .refine(
    (selections) =>
      new Set(selections.map((selection) => selection.field)).size === selections.length,
    "Cada campo deve aparecer apenas uma vez nas seleções.",
  )
  .describe(
    "Seleções explícitas: lista JSON de {field, values}, até 20 campos e 100 valores por campo. Os valores são buscas do Qlik e aceitam curingas (por exemplo, *). Não combine com bookmark.",
  );

export const measurementContextRequestSchema = z
  .object({
    bookmarkId: measurementBookmarkIdSchema.optional(),
    bookmarkName: measurementBookmarkNameSchema.optional(),
    selections: measurementSelectionsSchema.optional(),
  })
  .refine(
    (context) =>
      context.selections === undefined ||
      (context.bookmarkId === undefined && context.bookmarkName === undefined),
    { message: "Use bookmark ou seleções, nunca ambos.", path: ["selections"] },
  );

const measurementContextNameSchema = z.string().trim().min(1).max(128);

export const namedMeasurementContextRequestSchema = measurementContextRequestSchema.safeExtend({
  name: measurementContextNameSchema,
});
export type NamedMeasurementContextRequest = z.infer<typeof namedMeasurementContextRequestSchema>;

export const measurementContextsRequestSchema = z
  .array(namedMeasurementContextRequestSchema)
  .min(1)
  .max(32)
  .refine((contexts) => new Set(contexts.map((context) => context.name)).size === contexts.length, {
    message: "Os nomes dos contextos devem ser únicos.",
  });

const measurementContextResultFields = {
  name: measurementContextNameSchema,
  order: z.number().int().min(1).max(32),
  requestedContext: measurementContextRequestSchema,
  resolvedContext: analysisMeasurementContextSchema.optional(),
  durationMs: z.number().nonnegative(),
  reason: analysisMeasurementSkipReasonSchema.optional(),
  error: z.string().optional(),
};

export const analysisMeasurementContextResultSchema = z
  .discriminatedUnion("status", [
    z.object({
      ...measurementContextResultFields,
      status: z.enum(["completed", "partial"]),
      result: analysisObjectTimingsSchema,
    }),
    z.object({
      ...measurementContextResultFields,
      status: z.enum(["failed", "notExecuted"]),
      reason: analysisMeasurementSkipReasonSchema,
      result: z.null().optional(),
    }),
  ])
  .refine(
    (context) =>
      context.status === "failed" ||
      context.status === "notExecuted" ||
      context.result?.partial === (context.status === "partial"),
    { message: "O status do contexto deve corresponder à cobertura da medição.", path: ["status"] },
  );
export type AnalysisMeasurementContextResult = z.infer<
  typeof analysisMeasurementContextResultSchema
>;

export const analysisMeasurementBatchSchema = z
  .object({
    contexts: z.array(analysisMeasurementContextResultSchema).min(1).max(32),
    expectedObjectCount: z.number().int().nonnegative().optional(),
    measurementPhases: analysisMeasurementPhasesSchema,
    budgetScope: z.literal("batch"),
    durationMs: z.number().nonnegative(),
    partial: z.boolean(),
    partialReason: analysisMeasurementSkipReasonSchema.optional(),
  })
  .refine(
    (batch) =>
      new Set(batch.contexts.map((context) => context.name)).size === batch.contexts.length &&
      batch.contexts.every((context, index) => context.order === index + 1),
    {
      message: "Os contextos devem ter nomes únicos e seguir a ordem solicitada.",
      path: ["contexts"],
    },
  )
  .refine(
    (batch) => batch.partial === batch.contexts.some((context) => context.status !== "completed"),
    {
      message: "A cobertura do lote deve corresponder aos resultados dos contextos.",
      path: ["partial"],
    },
  );
export type AnalysisMeasurementBatch = z.infer<typeof analysisMeasurementBatchSchema>;

export const analysisStoredObjectTimingsSchema = z.union([
  analysisObjectTimingsSchema,
  analysisMeasurementBatchSchema,
]);
export type AnalysisStoredObjectTimings = z.infer<typeof analysisStoredObjectTimingsSchema>;

// --- Lifecycle (Option B) ---

// An analysis exists from the moment it is triggered. 'processing' rows carry no
// result payload; 'failed'/'cancelled' carry an error. Historical rows are all
// 'completed'.
export const appAnalysisStatusSchema = z.enum(["processing", "completed", "failed", "cancelled"]);

export type AppAnalysisStatus = z.infer<typeof appAnalysisStatusSchema>;

// Lifecycle fields shared by the result/detail/list schemas. Optional so older
// cached responses (and tests) still parse; the server always sends them now.
const lifecycleResultFields = {
  status: appAnalysisStatusSchema.optional(),
  error: z.string().nullable().optional(),
  jobId: z.string().uuid().nullable().optional(),
  startedAt: z.string().nullable().optional(),
  finishedAt: z.string().nullable().optional(),
};

// --- Full analysis result ---

export const appAnalysisResultSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  connectionId: z.string().uuid(),
  appId: z.string(),
  appName: z.string(),
  spaceName: z.string().nullable().optional(),
  // Optional: a 'processing'/'failed'/'cancelled' placeholder has no summary yet.
  summary: analysisSummarySchema.nullish(),
  ...lifecycleResultFields,
  fields: z
    .array(analysisFieldSchema)
    .nullish()
    .transform((v) => v ?? []),
  variables: z
    .array(analysisVariableSchema)
    .nullish()
    .transform((v) => v ?? []),
  dimensions: z
    .array(analysisDimensionSchema)
    .nullish()
    .transform((v) => v ?? []),
  measures: z
    .array(analysisMeasureSchema)
    .nullish()
    .transform((v) => v ?? []),
  objects: z
    .array(analysisObjectSchema)
    .nullish()
    .transform((v) => v ?? []),
  health: analysisHealthSchema.nullish(),
  analyzedAt: z.string(),
});

export type AppAnalysisResult = z.infer<typeof appAnalysisResultSchema>;

export const appAnalysisDetailSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  connectionId: z.string().uuid(),
  appId: z.string(),
  appName: z.string(),
  spaceName: z.string().nullable().optional(),
  // Optional: a 'processing'/'failed'/'cancelled' placeholder has no summary yet.
  summary: analysisSummarySchema.nullish(),
  ...lifecycleResultFields,
  fields: z
    .array(analysisFieldSchema)
    .nullish()
    .transform((v) => v ?? []),
  variables: z
    .array(analysisVariableSchema)
    .nullish()
    .transform((v) => v ?? []),
  dimensions: z
    .array(analysisDimensionSchema)
    .nullish()
    .transform((v) => v ?? []),
  measures: z
    .array(analysisMeasureSchema)
    .nullish()
    .transform((v) => v ?? []),
  // Carries the sheet each object renders on, so usage references can name the
  // sheet instead of only the object.
  objects: z
    .array(analysisObjectSchema)
    .nullish()
    .transform((v) => v ?? []),
  health: analysisHealthSchema.nullish(),
  // Qlik's own performance evaluation for this analysis, when one was asked for
  // (and only on the detail — it is a per-analysis extra, not part of the
  // result payload the worker saves). Nullish: every analysis stored before the
  // feature, and every analysis that opted out, has none.
  qlikEvaluation: qlikEvaluationSchema.nullish(),
  // Our own per-object measurement (frente 3), when the analysis opted into it.
  // Nullish: every analysis stored before the feature, and every analysis that
  // opted out, has none.
  objectTimings: analysisStoredObjectTimingsSchema.nullish(),
  analyzedAt: z.string(),
});

export type AppAnalysisDetail = z.infer<typeof appAnalysisDetailSchema>;

export const appAnalysisResultResponseSchema = z.object({
  data: appAnalysisDetailSchema,
});

export type AppAnalysisResultResponse = z.infer<typeof appAnalysisResultResponseSchema>;

// --- List response ---

export const appAnalysisListItemSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  connectionId: z.string().uuid(),
  appId: z.string(),
  appName: z.string(),
  spaceName: z.string().nullable().optional(),
  // Optional: a 'processing'/'failed'/'cancelled' placeholder has no summary yet.
  summary: analysisSummarySchema.nullish(),
  ...lifecycleResultFields,
  health: analysisHealthSummarySchema.nullish(),
  analyzedAt: z.string(),
});

export type AppAnalysisListItem = z.infer<typeof appAnalysisListItemSchema>;

export const listAppAnalysisResultsResponseSchema = z.object({
  data: z.array(appAnalysisListItemSchema),
  nextCursor: z.string().uuid().optional(),
});

export type ListAppAnalysisResultsResponse = z.infer<typeof listAppAnalysisResultsResponseSchema>;

// --- Diff between two analyses of the same app (computed on demand) ---

// Mirrors `analysisSummarySchema` field for field, plus the health score.
// `healthScoreDelta` is absent when either analysis predates health scoring.
export const analysisSummaryDeltaSchema = z.object({
  totalFieldsDelta: z.number(),
  usedFieldsDelta: z.number(),
  unusedFieldsDelta: z.number(),
  totalBytesDelta: z.number(),
  indexBytesDelta: z.number(),
  dataBytesDelta: z.number(),
  wastedBytesDelta: z.number(),
  wastedIndexBytesDelta: z.number(),
  indirectWastedBytesDelta: z.number(),
  indirectWastedIndexBytesDelta: z.number(),
  orphanWastedBytesDelta: z.number(),
  orphanWastedIndexBytesDelta: z.number(),
  staticByteSizeDelta: z.number(),
  appFileSizeDelta: z.number(),
  totalVariablesDelta: z.number(),
  usedVariablesDelta: z.number(),
  unusedVariablesDelta: z.number(),
  totalDimensionsDelta: z.number(),
  usedDimensionsDelta: z.number(),
  totalMeasuresDelta: z.number(),
  usedMeasuresDelta: z.number(),
  brokenDimensionsDelta: z.number(),
  brokenMeasuresDelta: z.number(),
  totalObjectsDelta: z.number(),
  healthScoreDelta: z.number().optional(),
});

export type AnalysisSummaryDelta = z.infer<typeof analysisSummaryDeltaSchema>;

// One entry per health category, always all five, in the canonical order.
// `null` marks a category the analysis did not score (e.g. the deferred
// object-complexity category).
export const analysisHealthCategoryDeltaSchema = z.object({
  id: analysisCategorySchema,
  from: z.number().nullable(),
  to: z.number().nullable(),
  delta: z.number().nullable(),
});

export type AnalysisHealthCategoryDelta = z.infer<typeof analysisHealthCategoryDeltaSchema>;

// Entries carry absolutes as well as deltas so the compare view can render
// "base → current" per row. `tableName` is omitted when the analysis carried no
// table for the field; multi-table fields join their tables with ", " (same
// representation as the fields table). `truncatedCount` is the number of
// entries dropped across added/removed/modified once each list hits its cap.
export const analysisFieldChangesSchema = z.object({
  added: z.array(
    z.object({ name: z.string(), byteSize: z.number(), tableName: z.string().optional() }),
  ),
  removed: z.array(
    z.object({ name: z.string(), byteSize: z.number(), tableName: z.string().optional() }),
  ),
  modified: z.array(
    z.object({
      name: z.string(),
      tableName: z.string().optional(),
      fromByteSize: z.number(),
      toByteSize: z.number(),
      byteSizeDelta: z.number(),
      fromCardinality: z.number(),
      toCardinality: z.number(),
      cardinalityDelta: z.number(),
    }),
  ),
  truncatedCount: z.number(),
});

export type AnalysisFieldChanges = z.infer<typeof analysisFieldChangesSchema>;

// Master items (variables/dimensions/measures) match strictly by Qlik ID. An
// item recreated with a new ID shows up as added + removed — accepted
// limitation.
export const analysisItemChangesSchema = z.object({
  added: z.array(z.object({ id: z.string(), label: z.string() })),
  removed: z.array(z.object({ id: z.string(), label: z.string() })),
  modified: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      brokenFrom: z.boolean(),
      brokenTo: z.boolean(),
      definitionChanged: z.boolean(),
      usedInCountDelta: z.number(),
    }),
  ),
  truncatedCount: z.number(),
});

export type AnalysisItemChanges = z.infer<typeof analysisItemChangesSchema>;

export const analysisDiffFindingSchema = z.object({
  id: analysisDiffFindingIdSchema,
  kind: analysisDiffKindSchema,
  severity: analysisSeveritySchema,
  confidence: analysisConfidenceSchema,
  category: analysisDiffCategorySchema,
  metrics: z.record(z.string(), z.union([z.number(), z.string()])),
  refs: z.array(analysisRefSchema),
});

export type AnalysisDiffFinding = z.infer<typeof analysisDiffFindingSchema>;

export const analysisDiffSchema = z.object({
  fromAnalysisId: z.string().uuid(),
  toAnalysisId: z.string().uuid(),
  fromAnalyzedAt: z.string(),
  toAnalyzedAt: z.string(),
  summaryDelta: analysisSummaryDeltaSchema,
  // Absent when either analysis has no health payload.
  healthCategoryDeltas: z.array(analysisHealthCategoryDeltaSchema).optional(),
  fieldChanges: analysisFieldChangesSchema,
  itemChanges: z.object({
    variables: analysisItemChangesSchema,
    dimensions: analysisItemChangesSchema,
    measures: analysisItemChangesSchema,
  }),
  findings: z.array(analysisDiffFindingSchema),
});

export type AnalysisDiff = z.infer<typeof analysisDiffSchema>;

export const appAnalysisCompareResponseSchema = z.object({
  data: analysisDiffSchema,
});

export type AppAnalysisCompareResponse = z.infer<typeof appAnalysisCompareResponseSchema>;

// GET /api/v1/analyses/:id/compare?baseId=<analysisId>
export const analysisCompareQuerySchema = z.object({
  baseId: z.string().uuid(),
});

export type AnalysisCompareQuery = z.infer<typeof analysisCompareQuerySchema>;

// --- Run logs (the worker's own execution log stream for an analysis, teed to
// the API in batches so the UI can show what happened live). Mirrors the QVD
// run-log schemas. ---
export const appAnalysisRunLogLevelSchema = z.enum(["info", "warn", "error"]);
export type AppAnalysisRunLogLevel = z.infer<typeof appAnalysisRunLogLevelSchema>;

export const appAnalysisRunLogSchema = z.object({
  // bigserial insert-order id, serialized as a string (the list's sort key +
  // pagination cursor)
  id: z.string(),
  ts: z.string(), // RFC3339 (the record's timestamp on the worker)
  level: appAnalysisRunLogLevelSchema,
  message: z.string(),
  // structured slog attrs (key → value); null when the record had none
  attrs: z.record(z.string(), z.unknown()).nullable(),
});
export type AppAnalysisRunLog = z.infer<typeof appAnalysisRunLogSchema>;

export const listAppAnalysisRunLogsResponseSchema = z.object({
  data: z.array(appAnalysisRunLogSchema),
  nextCursor: z.string().optional(), // the last row's id
});
export type ListAppAnalysisRunLogsResponse = z.infer<typeof listAppAnalysisRunLogsResponseSchema>;

// Worker streams the analysis's log entries in batches (append-only; ordering
// comes from the API-side bigserial id — a single writer per analysis flushes in
// order).
export const saveAppAnalysisRunLogsSchema = z.object({
  analysisId: z.string().uuid(),
  organizationId: z.string(),
  entries: z
    .array(
      z.object({
        ts: z.string(),
        level: appAnalysisRunLogLevelSchema,
        message: z.string().max(4000),
        attrs: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .max(500),
});
export type SaveAppAnalysisRunLogs = z.infer<typeof saveAppAnalysisRunLogsSchema>;

// --- Trigger input (POST /api/v1/analyses) ---

export const startAppAnalysisSchema = z
  .object({
    connectionId: z.string().uuid(),
    appId: z.string().min(1),
    appName: z.string(),
    spaceName: z.string().optional(),
    // Opt-in to Qlik's own performance evaluation, queued in parallel with the
    // static collection. ON by default: it is the product default, and the phase
    // is a no-op for apps the identity cannot evaluate (403 → 'forbidden' badge).
    includeQlikEvaluation: z.boolean().default(true),
    qlikEvaluationTimeoutSec: qlikEvaluationTimeoutSecSchema
      .optional()
      .describe(
        "Prazo da avaliação Qlik desde o disparo, em segundos (1–86400). Exige includeQlikEvaluation=true; ausente usa QLIK_EVALUATION_TIMEOUT_SEC do worker (padrão 7200, 2 horas).",
      ),
    // Opt-in to OUR own per-object measurement (session cubes). OFF by default,
    // unlike the Qlik evaluation: this one opens the app WITH data — 6.6 s cold
    // in the spike just to open — so it is a cost the user asks for.
    includeObjectMeasurement: z.boolean().default(false),
    measurementPhases: analysisMeasurementPhasesSchema
      .optional()
      .describe(
        'Fases de medição: lista JSON de "objects", "cache", "deconstruction" e "conditions", sem repetições e incluindo "objects". Exige includeObjectMeasurement=true; ausente executa todas as fases.',
      ),
    measurementBudgetSec: measurementDurationSecSchema
      .optional()
      .describe(
        "Orçamento total de medição em segundos (1–3600). Exige includeObjectMeasurement=true; ausente usa APP_MEASUREMENT_BUDGET_SEC do worker.",
      ),
    measurementCubeTimeoutSec: measurementDurationSecSchema
      .optional()
      .describe(
        "Timeout por cubo em segundos (1–3600). Exige includeObjectMeasurement=true; ausente usa APP_MEASUREMENT_CUBE_TIMEOUT_SEC do worker.",
      ),
    measurementContexts: measurementContextsRequestSchema
      .optional()
      .describe(
        "Contextos nomeados: lista JSON de {name, bookmarkId?, bookmarkName?, selections?}, de 1 a 32 nomes únicos (até 128 caracteres). Exige includeObjectMeasurement=true; não combine com o contexto único. Orçamento e fases são compartilhados pelo lote; sem bookmark ou seleções, usa o padrão da aplicação ou estado limpo.",
      ),
    measurementBookmarkId: measurementBookmarkIdSchema.optional(),
    measurementBookmarkName: measurementBookmarkNameSchema.optional(),
    measurementSelections: measurementSelectionsSchema.optional(),
  })
  .refine(
    (input) =>
      input.measurementContexts === undefined ||
      (input.measurementBookmarkId === undefined &&
        input.measurementBookmarkName === undefined &&
        input.measurementSelections === undefined),
    {
      message: "Use contextos nomeados ou o contexto único, nunca ambos.",
      path: ["measurementContexts"],
    },
  )
  .refine((input) => input.includeQlikEvaluation || input.qlikEvaluationTimeoutSec === undefined, {
    message: "O prazo da avaliação exige includeQlikEvaluation=true.",
    path: ["qlikEvaluationTimeoutSec"],
  })
  .refine(
    (input) =>
      input.measurementSelections === undefined ||
      (input.measurementBookmarkId === undefined && input.measurementBookmarkName === undefined),
    { message: "Use bookmark ou seleções, nunca ambos.", path: ["measurementSelections"] },
  )
  .refine(
    (input) =>
      input.includeObjectMeasurement ||
      (input.measurementPhases === undefined &&
        input.measurementBudgetSec === undefined &&
        input.measurementCubeTimeoutSec === undefined &&
        input.measurementBookmarkId === undefined &&
        input.measurementBookmarkName === undefined &&
        input.measurementSelections === undefined &&
        input.measurementContexts === undefined),
    {
      message: "As fases, o contexto e os limites de medição exigem includeObjectMeasurement=true.",
      path: ["includeObjectMeasurement"],
    },
  );

// z.input, not z.infer: this is the body a CLIENT sends, and `.default(true)`
// makes includeQlikEvaluation required on the OUTPUT side only. The API still
// receives the parsed (defaulted) object, which is assignable to this.
export type StartAppAnalysisInput = z.input<typeof startAppAnalysisSchema>;

// --- Job payload ---

export const analyzeAppPayloadSchema = z
  .object({
    connectionId: z.string().uuid(),
    appId: z.string(),
    appName: z.string(),
    spaceName: z.string().optional(),
    // Lifecycle (Option B): when present, the worker updates this placeholder row
    // and streams its logs. Absent for legacy in-flight jobs (plain insert at the
    // end, no logs) — safe mid-deploy.
    analysisId: z.string().uuid().optional(),
    // See startAppAnalysisSchema. Absent on jobs created before this deploy; the
    // worker reads it as a *bool and treats absent as true, matching this default.
    includeQlikEvaluation: z.boolean().default(true),
    qlikEvaluationTimeoutSec: qlikEvaluationTimeoutSecSchema.optional(),
    // See startAppAnalysisSchema. The worker reads this one as a plain bool: the
    // default is false, which is Go's zero value, so absent already means off.
    includeObjectMeasurement: z.boolean().default(false),
    measurementPhases: analysisMeasurementPhasesSchema.optional(),
    measurementBudgetSec: measurementDurationSecSchema.optional(),
    measurementCubeTimeoutSec: measurementDurationSecSchema.optional(),
    measurementContext: measurementContextRequestSchema.optional(),
    measurementContexts: measurementContextsRequestSchema.optional(),
  })
  .refine(
    (input) => input.measurementContexts === undefined || input.measurementContext === undefined,
    {
      message: "Use contextos nomeados ou o contexto único, nunca ambos.",
      path: ["measurementContexts"],
    },
  )
  .refine((input) => input.includeQlikEvaluation || input.qlikEvaluationTimeoutSec === undefined, {
    message: "O prazo da avaliação exige includeQlikEvaluation=true.",
    path: ["qlikEvaluationTimeoutSec"],
  })
  .refine(
    (input) =>
      input.includeObjectMeasurement ||
      (input.measurementPhases === undefined &&
        input.measurementContext === undefined &&
        input.measurementContexts === undefined &&
        input.measurementBudgetSec === undefined &&
        input.measurementCubeTimeoutSec === undefined),
    {
      message: "As fases, o contexto e os limites de medição exigem includeObjectMeasurement=true.",
      path: ["includeObjectMeasurement"],
    },
  );

export type AnalyzeAppPayload = z.infer<typeof analyzeAppPayloadSchema>;

// --- List/log query strings (the API validates them; the CLI validates flags). ---
export const appAnalysisListQuerySchema = z
  .object({
    connectionId: z.string().uuid().optional(),
    appId: z
      .string()
      .min(1)
      .max(128)
      .regex(/^[A-Za-z0-9_\-{}.]+$/)
      .optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    status: appAnalysisStatusSchema.optional(),
  })
  .passthrough();

// level = MINIMUM severity shown ("info" → everything, "warn" → warn+error).
export const appAnalysisLogsQuerySchema = z
  .object({ level: appAnalysisRunLogLevelSchema.optional() })
  .passthrough();
